import React, { useState } from 'react';

// Light Theme and styling
const AghrbaLightTheme = {
  colors: {
    // Primary colors - teal/navy blue (slightly lighter for light mode)
    primary: {
      main: '#0F3A61',
      light: '#2B5885',
      dark: '#072747',
      contrastText: '#FFFFFF',
    },
    // Secondary/accent colors - gold/yellow (same as dark theme)
    secondary: {
      main: '#f2c526',
      light: '#FFD859',
      dark: '#D9AD1F',
      contrastText: '#0F3A61',
    },
    // Background colors - light mode
    background: {
      default: '#F8FAFD',
      paper: '#FFFFFF',
      light: '#EEF2F7',
    },
    // Text colors - dark text for light mode
    text: {
      primary: '#0F3A61',
      secondary: '#4A6785',
      disabled: '#90A0B7',
      accent: '#D9AD1F',
    },
    // Status colors
    success: '#43A047',
    warning: '#FB8C00',
    error: '#E53935',
    info: '#1E88E5',
  },
  
  // Animation keyframes (same as dark theme)
  keyframes: {
    float: `
      @keyframes float {
        0% { transform: translateY(0); }
        50% { transform: translateY(-10px); }
        100% { transform: translateY(0); }
      }
    `,
    glow: `
      @keyframes glow {
        0% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
        50% { box-shadow: 0 0 20px rgba(228, 180, 76, 0.6), 0 0 15px rgba(228, 180, 76, 0.3); }
        100% { box-shadow: 0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1); }
      }
    `,
    shine: `
      @keyframes shine {
        0% { background-position: -100% 0; }
        100% { background-position: 200% 0; }
      }
    `,
    gradient: `
      @keyframes gradient {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
    `,
  }
};

// Shared styles for light theme
const styles = {
  gradientText: {
    background: `linear-gradient(90deg, ${AghrbaLightTheme.colors.secondary.dark} 0%, ${AghrbaLightTheme.colors.secondary.main} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    color: 'transparent',
    display: 'inline-block',
  },
  gradientBorder: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '2px',
      background: `linear-gradient(135deg, ${AghrbaLightTheme.colors.secondary.main}, ${AghrbaLightTheme.colors.secondary.light}, ${AghrbaLightTheme.colors.secondary.main})`,
      backgroundSize: '300% 300%',
      animation: 'gradient 4s ease infinite',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      zIndex: 0,
    },
  },
  floatingAnimation: {
    animation: 'float 4s ease-in-out infinite',
  },
  glowingEffect: {
    animation: 'glow 3s infinite',
  },
  magicalBackground: {
    background: `linear-gradient(135deg, ${AghrbaLightTheme.colors.background.light} 0%, ${AghrbaLightTheme.colors.background.paper} 100%)`,
  }
};

// Create mock icons for the design system
const MockIcons = {
  Search: () => (
    <div style={{width: 20, height: 20, borderRadius: '50%', border: '2px solid currentColor'}} />
  ),
  Add: () => (
    <div style={{width: 20, height: 20, position: 'relative'}}>
      <div style={{position: 'absolute', width: '100%', height: 2, background: 'currentColor', top: 9}}></div>
      <div style={{position: 'absolute', width: 2, height: '100%', background: 'currentColor', left: 9}}></div>
    </div>
  ),
  Menu: () => (
    <div style={{width: 20, height: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
      <div style={{height: 2, background: 'currentColor', width: '100%'}}></div>
      <div style={{height: 2, background: 'currentColor', width: '100%'}}></div>
      <div style={{height: 2, background: 'currentColor', width: '100%'}}></div>
    </div>
  ),
  Globe: () => (
    <div style={{
      width: 20, 
      height: 20, 
      borderRadius: '50%', 
      border: '2px solid #f2c526',
      position: 'relative'
    }}>
      <div style={{
        position: 'absolute',
        width: '100%',
        height: 2,
        background: '#f2c526',
        top: 8
      }}></div>
      <div style={{
        position: 'absolute',
        width: 2,
        height: '100%',
        background: '#f2c526',
        left: 8
      }}></div>
    </div>
  ),
};

// Components
const Typography = ({ variant, children, style, ...props }) => {
  const getStyles = () => {
    switch(variant) {
      case 'h1':
        return { 
          fontFamily: '"Playfair Display", serif',
          fontSize: '2.5rem',
          fontWeight: 700,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'h2':
        return { 
          fontFamily: '"Playfair Display", serif',
          fontSize: '2rem',
          fontWeight: 700,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'h3':
        return { 
          fontFamily: '"Playfair Display", serif',
          fontSize: '1.75rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'h4':
        return { 
          fontFamily: '"Playfair Display", serif',
          fontSize: '1.5rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'h5':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'h6':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '1rem',
          fontWeight: 600,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'subtitle1':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '1rem',
          fontWeight: 500,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'subtitle2':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '0.875rem',
          fontWeight: 500,
          marginBottom: '0.5rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'body1':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '1rem',
          lineHeight: 1.6,
          marginBottom: '1rem',
          color: AghrbaLightTheme.colors.text.primary
        };
      case 'body2':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '0.875rem',
          lineHeight: 1.6,
          marginBottom: '1rem',
          color: AghrbaLightTheme.colors.text.secondary
        };
      case 'caption':
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '0.75rem',
          lineHeight: 1.4,
          color: AghrbaLightTheme.colors.text.secondary
        };
      default:
        return { 
          fontFamily: '"Poppins", sans-serif',
          fontSize: '1rem',
          color: AghrbaLightTheme.colors.text.primary
        };
    }
  };
  
  return (
    <div style={{ ...getStyles(), ...style }} {...props}>
      {children}
    </div>
  );
};

const Button = ({ 
  variant = 'contained', 
  color = 'primary', 
  size = 'medium',
  startIcon,
  endIcon,
  children,
  isGlowing = false,
  disabled = false,
  style,
  ...props 
}) => {
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          padding: '0.5rem 1rem',
          fontSize: '0.75rem',
        };
      case 'large':
        return { 
          padding: '0.75rem 2rem',
          fontSize: '1rem',
        };
      default: // medium
        return { 
          padding: '0.625rem 1.5rem',
          fontSize: '0.875rem',
        };
    }
  };
  
  const getVariantStyles = () => {
    // Colors based on variant and color prop
    const colors = {
      primary: {
        main: AghrbaLightTheme.colors.primary.main,
        light: AghrbaLightTheme.colors.primary.light,
        contrastText: AghrbaLightTheme.colors.primary.contrastText,
      },
      secondary: {
        main: AghrbaLightTheme.colors.secondary.main,
        light: AghrbaLightTheme.colors.secondary.light,
        contrastText: AghrbaLightTheme.colors.secondary.contrastText,
      }
    };
    
    const selectedColor = colors[color] || colors.primary;
    
    switch(variant) {
      case 'contained':
        return { 
          background: `linear-gradient(45deg, ${selectedColor.main} 0%, ${selectedColor.light} 100%)`,
          color: selectedColor.contrastText,
          border: 'none',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
            filter: 'brightness(1.1)',
          }
        };
      case 'outlined':
        return { 
          background: 'transparent',
          color: color === 'secondary' ? AghrbaLightTheme.colors.secondary.main : AghrbaLightTheme.colors.primary.main,
          border: `2px solid ${color === 'secondary' ? AghrbaLightTheme.colors.secondary.main : AghrbaLightTheme.colors.primary.main}`,
          '&:hover': {
            background: color === 'secondary' ? 'rgba(228, 180, 76, 0.1)' : 'rgba(15, 58, 97, 0.05)',
          }
        };
      case 'text':
        return { 
          background: 'transparent',
          color: color === 'secondary' ? AghrbaLightTheme.colors.secondary.main : AghrbaLightTheme.colors.primary.main,
          border: 'none',
          '&:hover': {
            background: color === 'secondary' ? 'rgba(228, 180, 76, 0.1)' : 'rgba(15, 58, 97, 0.05)',
          }
        };
      default:
        return {};
    }
  };
  
  const getDisabledStyles = () => {
    if (!disabled) return {};
    
    return {
      opacity: 0.6,
      cursor: 'not-allowed',
      boxShadow: 'none',
      '&:hover': {
        boxShadow: 'none',
        filter: 'none',
        background: variant === 'contained' ? 
          `linear-gradient(45deg, ${AghrbaLightTheme.colors.primary.main} 0%, ${AghrbaLightTheme.colors.primary.light} 100%)` : 
          undefined,
      }
    };
  };
  
  const getMagicalEffects = () => {
    if (!isGlowing) return {};
    
    return {
      animation: 'glow 3s infinite',
      '@keyframes glow': {
        '0%': { boxShadow: '0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1)' },
        '50%': { boxShadow: '0 0 20px rgba(228, 180, 76, 0.6), 0 0 15px rgba(228, 180, 76, 0.3)' },
        '100%': { boxShadow: '0 0 5px rgba(228, 180, 76, 0.4), 0 0 10px rgba(228, 180, 76, 0.1)' },
      },
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
        transition: 'left 0.7s',
      },
      '&:hover::before': {
        left: '100%',
      },
    };
  };
  
  // Combine all styles
  const buttonStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Poppins", sans-serif',
    fontWeight: 600,
    borderRadius: '30px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    gap: '0.5rem',
    ...getSizeStyles(),
    ...getVariantStyles(),
    ...getDisabledStyles(),
    ...getMagicalEffects(),
    ...style
  };
  
  return (
    <button style={buttonStyles} disabled={disabled} {...props}>
      {startIcon && <span className="button-icon">{startIcon}</span>}
      {children}
      {endIcon && <span className="button-icon">{endIcon}</span>}
    </button>
  );
};

const Card = ({ 
  children, 
  isGlowing = false, 
  isFloating = false,
  gradientBorder = false,
  style,
  ...props 
}) => {
  const cardStyles = {
    backgroundColor: AghrbaLightTheme.colors.background.paper,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.08)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    position: 'relative',
    ...(isGlowing && {
      boxShadow: `0 8px 16px rgba(0, 0, 0, 0.08), 0 0 10px ${AghrbaLightTheme.colors.secondary.main}40, 0 0 30px ${AghrbaLightTheme.colors.secondary.main}20`,
    }),
    ...(isFloating && {
      animation: 'float 4s ease-in-out infinite',
    }),
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: '0 12px 20px rgba(0, 0, 0, 0.12)',
    },
    ...style
  };
  
  // Gradient border effect
  const gradientBorderStyles = gradientBorder ? {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      padding: '2px',
      background: `linear-gradient(135deg, ${AghrbaLightTheme.colors.secondary.main}, ${AghrbaLightTheme.colors.secondary.light}, ${AghrbaLightTheme.colors.secondary.main})`,
      backgroundSize: '300% 300%',
      animation: 'gradient 5s ease infinite',
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      zIndex: 0,
    }
  } : {};
  
  return (
    <div style={{...cardStyles, ...gradientBorderStyles}} {...props}>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {children}
      </div>
    </div>
  );
};

const TextField = ({ 
  label, 
  placeholder,
  value,
  onChange,
  type = 'text',
  size = 'medium',
  fullWidth = false,
  style,
  ...props 
}) => {
  const [focused, setFocused] = useState(false);
  
  const getSizeStyles = () => {
    switch(size) {
      case 'small':
        return { 
          padding: '8px 12px',
          fontSize: '0.875rem',
        };
      case 'large':
        return { 
          padding: '12px 16px',
          fontSize: '1rem',
        };
      default: // medium
        return { 
          padding: '10px 14px',
          fontSize: '0.875rem',
        };
    }
  };
  
  const containerStyles = {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '1rem',
    width: fullWidth ? '100%' : 'auto',
    ...style
  };
  
  const labelStyles = {
    fontFamily: '"Poppins", sans-serif',
    fontSize: '0.875rem',
    color: AghrbaLightTheme.colors.text.secondary,
    marginBottom: '0.5rem'
  };
  
  const inputStyles = {
    fontFamily: '"Poppins", sans-serif',
    color: AghrbaLightTheme.colors.text.primary,
    backgroundColor: AghrbaLightTheme.colors.background.light,
    border: focused ? 
      `1px solid ${AghrbaLightTheme.colors.secondary.main}` : 
      '1px solid transparent',
    borderRadius: '30px',
    transition: 'all 0.3s ease',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    ...getSizeStyles(),
    '&::placeholder': {
      color: AghrbaLightTheme.colors.text.disabled,
    },
    '&:hover': {
      backgroundColor: AghrbaLightTheme.colors.background.light,
    },
    '&:focus': {
      border: `1px solid ${AghrbaLightTheme.colors.secondary.main}`,
      boxShadow: `0 0 0 3px ${AghrbaLightTheme.colors.secondary.main}40`,
    }
  };
  
  return (
    <div style={containerStyles}>
      {label && <label style={labelStyles}>{label}</label>}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={inputStyles}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        {...props}
      />
    </div>
  );
};

// Example component that demonstrates all the components - Light Mode Version
const AghrbaLightDesignSystem = () => {
  const [activeTab, setActiveTab] = useState(0);
  
  const tabs = [
    { label: 'Theme', content: 'Theme content' },
    { label: 'Typography', content: 'Typography content' },
    { label: 'Buttons', content: 'Buttons content' },
    { label: 'Cards', content: 'Cards content' },
  ];
  
  // Main container style
  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: AghrbaLightTheme.colors.background.default,
    fontFamily: '"Poppins", sans-serif',
    color: AghrbaLightTheme.colors.text.primary,
    padding: '40px 0'
  };
  
  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h3" style={styles.gradientText}>
          Aghrba Light Mode Design System
        </Typography>
        <Typography variant="body1">
          The comprehensive design system for Aghrba's marketing and software platform. This light mode version maintains the magical elements while providing a bright, airy alternative to the dark theme.
        </Typography>
      </div>
      
      {/* Color Palette */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Color Palette
        </Typography>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '20px'
        }}>
          {/* Primary Colors */}
          <Card>
            <div style={{ 
              height: '120px', 
              backgroundColor: AghrbaLightTheme.colors.primary.main,
              display: 'flex',
              alignItems: 'flex-end',
              padding: '20px'
            }}>
              <Typography variant="h6" style={{ color: '#FFFFFF' }}>Primary</Typography>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.primary.main,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">main: {AghrbaLightTheme.colors.primary.main}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.primary.light,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">light: {AghrbaLightTheme.colors.primary.light}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.primary.dark,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">dark: {AghrbaLightTheme.colors.primary.dark}</Typography>
              </div>
            </div>
          </Card>
          
          {/* Secondary Colors */}
          <Card>
            <div style={{ 
              height: '120px', 
              backgroundColor: AghrbaLightTheme.colors.secondary.main,
              display: 'flex',
              alignItems: 'flex-end',
              padding: '20px'
            }}>
              <Typography variant="h6" style={{ color: AghrbaLightTheme.colors.primary.main }}>Secondary</Typography>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.secondary.main,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">main: {AghrbaLightTheme.colors.secondary.main}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.secondary.light,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">light: {AghrbaLightTheme.colors.secondary.light}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.secondary.dark,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">dark: {AghrbaLightTheme.colors.secondary.dark}</Typography>
              </div>
            </div>
          </Card>
          
          {/* Background Colors */}
          <Card>
            <div style={{ 
              height: '120px', 
              backgroundColor: AghrbaLightTheme.colors.background.default,
              display: 'flex',
              alignItems: 'flex-end',
              padding: '20px'
            }}>
              <Typography variant="h6">Background</Typography>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.background.default,
                  border: `1px solid ${AghrbaLightTheme.colors.text.disabled}`,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">default: {AghrbaLightTheme.colors.background.default}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '10px'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.background.paper,
                  border: `1px solid ${AghrbaLightTheme.colors.text.disabled}`,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">paper: {AghrbaLightTheme.colors.background.paper}</Typography>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center'
              }}>
                <div style={{ 
                  width: '24px', 
                  height: '24px', 
                  borderRadius: '4px',
                  backgroundColor: AghrbaLightTheme.colors.background.light,
                  marginRight: '10px'
                }} />
                <Typography variant="body2">light: {AghrbaLightTheme.colors.background.light}</Typography>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Typography */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Typography
        </Typography>
        
        <Card style={{ padding: '30px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '40px'
          }}>
            <div>
              <Typography variant="h1">Heading 1</Typography>
              <Typography variant="h2">Heading 2</Typography>
              <Typography variant="h3">Heading 3</Typography>
              <Typography variant="h4">Heading 4</Typography>
              <Typography variant="h5">Heading 5</Typography>
              <Typography variant="h6">Heading 6</Typography>
            </div>
            
            <div>
              <Typography variant="subtitle1">
                Subtitle 1 - Medium weight text that works for small headings
              </Typography>
              <Typography variant="subtitle2">
                Subtitle 2 - Smaller version with medium weight
              </Typography>
              <Typography variant="body1">
                Body 1 - The standard text size for body copy. This size works well for most content and maintains good readability. The line height is optimized for multiline text blocks.
              </Typography>
              <Typography variant="body2">
                Body 2 - A smaller text size useful for secondary information or dense interfaces where space is limited. Still maintains readability while being more compact.
              </Typography>
              <Typography variant="caption">
                Caption text - Small text used for captions or supplementary information
              </Typography>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Buttons */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Magical Buttons
        </Typography>
        
        <Card style={{ padding: '30px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '40px'
          }}>
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Button Variants</Typography>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <Button>Primary</Button>
                <Button color="secondary">Secondary</Button>
                <Button variant="outlined">Outlined</Button>
                <Button variant="outlined" color="secondary">Outlined Gold</Button>
              </div>
              
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Enchanted Buttons with Magic</Typography>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <Button isGlowing>Magical Primary</Button>
                <Button color="secondary" isGlowing>Glowing Gold</Button>
                <Button variant="outlined" color="secondary" startIcon={<MockIcons.Add />}>
                  Create Project
                </Button>
              </div>
            </div>
            
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Button Sizes</Typography>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="small">Small</Button>
                <Button>Medium</Button>
                <Button size="large">Large</Button>
              </div>
              
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Button States</Typography>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <Button disabled>Disabled</Button>
                {/* This would show a loading state in real implementation */}
                <Button style={{ position: 'relative' }}>
                  <span style={{ opacity: 0 }}>Loading</span>
                  <span style={{ 
                    position: 'absolute',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'currentColor transparent currentColor transparent',
                    animation: 'spin 1.2s linear infinite',
                  }}></span>
                </Button>
                <Button style={{ backgroundColor: AghrbaLightTheme.colors.success }}>Success</Button>
                <Button style={{ backgroundColor: AghrbaLightTheme.colors.error }}>Error</Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Cards */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Magical Cards
        </Typography>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px'
        }}>
          {/* Standard Card */}
          <Card>
            <div style={{ 
              height: '180px',
              backgroundColor: AghrbaLightTheme.colors.background.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `radial-gradient(circle at 30% 40%, ${AghrbaLightTheme.colors.primary.light}40 0%, transparent 70%)`,
              }}></div>
              <Typography variant="h5" style={{ position: 'relative', zIndex: 1 }}>
                Standard Card
              </Typography>
            </div>
            
            <div style={{ padding: '20px' }}>
              <Typography variant="h6" style={{ marginBottom: '8px' }}>
                Card Title
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                This is a standard card that uses the background paper color and provides a clean container for content.
              </Typography>
              <Button variant="outlined" size="small" color="secondary">
                View Details
              </Button>
            </div>
          </Card>
          
          {/* Magical Card */}
          <Card gradientBorder>
            <div style={{ 
              height: '180px',
              background: `linear-gradient(135deg, ${AghrbaLightTheme.colors.primary.dark} 0%, ${AghrbaLightTheme.colors.primary.main} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `radial-gradient(circle at center, rgba(228, 180, 76, 0.2) 0%, transparent 70%)`,
                transform: 'rotate(30deg)',
                width: '200%',
                height: '200%',
                top: '-50%',
                left: '-50%',
              }}></div>
              <Typography variant="h5" style={{ position: 'relative', zIndex: 1, color: '#FFFFFF' }}>
                Magical Card
              </Typography>
            </div>
            
            <div style={{ padding: '20px' }}>
              <Typography variant="h6" style={{ marginBottom: '8px' }}>
                Enchanted Experience
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                This magical card features a gradient border animation and hover effects that reveal actions.
              </Typography>
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                <Button color="secondary">
                  Discover Magic
                </Button>
              </div>
            </div>
          </Card>
          
          {/* Floating Card */}
          <Card isGlowing isFloating>
            <div style={{ 
              height: '180px',
              background: `linear-gradient(135deg, ${AghrbaLightTheme.colors.secondary.dark} 0%, ${AghrbaLightTheme.colors.secondary.main} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <Typography variant="h5" style={{ 
                position: 'relative', 
                zIndex: 1,
                color: AghrbaLightTheme.colors.primary.dark,
                fontWeight: 700
              }}>
                Floating Card
              </Typography>
            </div>
            
            <div style={{ padding: '20px' }}>
              <Typography variant="h6" style={{ 
                marginBottom: '8px', 
                ...styles.gradientText
              }}>
                Premium Feature
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '16px' }}>
                This card floats with a soft glow effect and premium styling to highlight important features.
              </Typography>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginTop: '16px'
              }}>
                <Typography variant="caption" style={{ color: AghrbaLightTheme.colors.text.accent }}>
                  Starting at $50/mo
                </Typography>
                <Button variant="text" size="small" color="secondary">
                  Learn More
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Example Project Card */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 20px',
        marginBottom: '40px'
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Example Components
        </Typography>
        
        <Typography variant="h5" style={{ marginBottom: '20px' }}>
          Project Card
        </Typography>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '20px'
        }}>
          {/* Standard Project Card */}
          <Card>
            <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 2 }}>
              <div style={{ 
                backgroundColor: `${AghrbaLightTheme.colors.success}30`,
                color: AghrbaLightTheme.colors.success,
                border: `1px solid ${AghrbaLightTheme.colors.success}50`,
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '0.75rem',
                fontWeight: 500,
                display: 'inline-block',
              }}>
                active
              </div>
            </div>
            
            <div style={{ padding: '20px' }}>
              <Typography variant="h6" style={{ 
                marginBottom: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                Brand Redesign
              </Typography>
              <Typography variant="body2" style={{ 
                height: '40px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                marginBottom: '16px'
              }}>
                Complete brand identity refresh for a tech company including logo, website, and marketing materials.
              </Typography>
              
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  Progress
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', marginRight: '8px' }}>
                    <div style={{ 
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: AghrbaLightTheme.colors.background.light,
                      overflow: 'hidden',
                    }}>
                      <div style={{ 
                        width: '65%',
                        height: '100%',
                        background: `linear-gradient(90deg, ${AghrbaLightTheme.colors.primary.main}, ${AghrbaLightTheme.colors.secondary.main})`,
                      }}></div>
                    </div>
                  </div>
                  <Typography variant="body2">
                    65%
                  </Typography>
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.text.secondary 
                    }}></div>
                  </div>
                  <Typography variant="body2">
                    Deadline: Dec 31, 2025
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.text.secondary 
                    }}></div>
                  </div>
                  <Typography variant="body2">
                    Estimated: 120 hours
                  </Typography>
                </div>
              </div>
              
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${AghrbaLightTheme.colors.background.light}`,
              }}>
                <div style={{ display: 'flex' }}>
                  {/* Avatar Group Simulation */}
                  <div style={{ display: 'flex' }}>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.primary.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      border: `2px solid ${AghrbaLightTheme.colors.background.paper}`,
                      zIndex: 3,
                    }}>
                      JS
                    </div>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.secondary.main,
                      color: AghrbaLightTheme.colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '-10px',
                      border: `2px solid ${AghrbaLightTheme.colors.background.paper}`,
                      zIndex: 2,
                    }}>
                      LD
                    </div>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.primary.dark,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '-10px',
                      border: `2px solid ${AghrbaLightTheme.colors.background.paper}`,
                      zIndex: 1,
                    }}>
                      +2
                    </div>
                  </div>
                </div>
                
                <button style={{ 
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  color: AghrbaLightTheme.colors.text.secondary,
                  cursor: 'pointer',
                }}>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: 'currentColor' 
                    }}></div>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: 'currentColor' 
                    }}></div>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: 'currentColor' 
                    }}></div>
                  </div>
                </button>
              </div>
            </div>
          </Card>
          
          {/* Premium Project Card */}
          <Card gradientBorder isGlowing>
            <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 2 }}>
              <div style={{ 
                backgroundColor: `${AghrbaLightTheme.colors.error}30`,
                color: AghrbaLightTheme.colors.error,
                border: `1px solid ${AghrbaLightTheme.colors.error}50`,
                borderRadius: '16px',
                padding: '4px 12px',
                fontSize: '0.75rem',
                fontWeight: 500,
                display: 'inline-block',
              }}>
                high priority
              </div>
            </div>
            
            <div style={{ padding: '20px' }}>
              <Typography variant="h6" style={{ 
                marginBottom: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...styles.gradientText
              }}>
                Mobile App Development
              </Typography>
              <Typography variant="body2" style={{ 
                height: '40px',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                marginBottom: '16px'
              }}>
                Creating a cross-platform mobile application with real-time features and elegant UI.
              </Typography>
              
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  Progress
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ width: '100%', marginRight: '8px' }}>
                    <div style={{ 
                      height: '6px',
                      borderRadius: '3px',
                      backgroundColor: AghrbaLightTheme.colors.background.light,
                      overflow: 'hidden',
                    }}>
                      <div style={{ 
                        width: '25%',
                        height: '100%',
                        background: `linear-gradient(90deg, ${AghrbaLightTheme.colors.secondary.main}, ${AghrbaLightTheme.colors.secondary.light})`,
                      }}></div>
                    </div>
                  </div>
                  <Typography variant="body2" style={{ color: AghrbaLightTheme.colors.secondary.main }}>
                    25%
                  </Typography>
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.text.secondary 
                    }}></div>
                  </div>
                  <Typography variant="body2">
                    Deadline: Nov 15, 2025
                  </Typography>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center'
                  }}>
                    <div style={{ 
                      width: '4px', 
                      height: '4px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.text.secondary 
                    }}></div>
                  </div>
                  <Typography variant="body2">
                    Estimated: 220 hours
                  </Typography>
                </div>
              </div>
              
              <div style={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${AghrbaLightTheme.colors.background.light}`,
              }}>
                <div style={{ display: 'flex' }}>
                  {/* Avatar Group Simulation */}
                  <div style={{ display: 'flex' }}>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.secondary.main,
                      color: AghrbaLightTheme.colors.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      border: `2px solid ${AghrbaLightTheme.colors.secondary.light}`,
                      zIndex: 3,
                    }}>
                      AS
                    </div>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.primary.main,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '-10px',
                      border: `2px solid ${AghrbaLightTheme.colors.background.paper}`,
                      zIndex: 2,
                    }}>
                      KL
                    </div>
                    <div style={{ 
                      width: '30px', 
                      height: '30px', 
                      borderRadius: '50%', 
                      backgroundColor: AghrbaLightTheme.colors.primary.dark,
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '-10px',
                      border: `2px solid ${AghrbaLightTheme.colors.background.paper}`,
                      zIndex: 1,
                    }}>
                      +4
                    </div>
                  </div>
                </div>
                
                <Button variant="outlined" size="small" color="secondary" style={{ 
                  fontSize: '0.75rem',
                  padding: '4px 12px',
                  minWidth: 0
                }}>
                  View
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Header Bar Example */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto 40px',
        padding: '0 20px',
      }}>
        <Typography variant="h5" style={{ marginBottom: '20px' }}>
          Application Header
        </Typography>
        
        <Card style={{ 
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button style={{ 
              background: 'none',
              border: 'none',
              color: AghrbaLightTheme.colors.text.primary,
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <MockIcons.Menu />
            </button>
            
            <Typography variant="h6" style={{ 
              fontFamily: '"Playfair Display", serif', 
              ...styles.gradientText,
              fontWeight: 700,
              margin: 0
            }}>
              Aghrba
            </Typography>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ 
              backgroundColor: AghrbaLightTheme.colors.background.light,
              border: 'none',
              borderRadius: '20px',
              padding: '8px 12px',
              display: 'flex',
              alignItems: 'center',
              color: AghrbaLightTheme.colors.text.disabled,
              width: '200px',
            }}>
              <MockIcons.Search />
              <input
                placeholder="Search..."
                style={{
                  background: 'none',
                  border: 'none',
                  color: AghrbaLightTheme.colors.text.primary,
                  outline: 'none',
                  marginLeft: '8px',
                  width: '100%',
                  '&::placeholder': {
                    color: AghrbaLightTheme.colors.text.disabled,
                  },
                }}
              />
            </div>
            
            <button style={{ 
              background: 'none',
              border: 'none',
              color: AghrbaLightTheme.colors.text.primary,
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid currentColor' }} />
            </button>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginLeft: '8px',
              padding: '4px 8px',
              borderRadius: '20px',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: AghrbaLightTheme.colors.background.light,
              }
            }}>
              <div style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '50%', 
                backgroundColor: AghrbaLightTheme.colors.secondary.main,
                color: AghrbaLightTheme.colors.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 'bold',
              }}>
                AS
              </div>
              <Typography variant="body2" style={{ fontWeight: 500, margin: 0 }}>
                Alaaeldin
              </Typography>
              <div style={{ 
                width: 0, 
                height: 0, 
                borderLeft: '6px solid transparent', 
                borderRight: '6px solid transparent', 
                borderTop: '8px solid currentColor' 
              }} />
            </div>
          </div>
        </Card>
      </div>
      
      {/* Navigation Tabs */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '40px auto',
        padding: '0 20px',
      }}>
        <Typography variant="h5" style={{ marginBottom: '20px' }}>
          Navigation Components
        </Typography>
        
        <Card style={{ marginBottom: '30px', overflow: 'hidden' }}>
          <div style={{ 
            borderBottom: `1px solid ${AghrbaLightTheme.colors.background.light}`,
            display: 'flex',
            position: 'relative',
          }}>
            {tabs.map((tab, index) => (
              <div
                key={index}
                onClick={() => setActiveTab(index)}
                style={{
                  padding: '16px 24px',
                  cursor: 'pointer',
                  color: activeTab === index ? AghrbaLightTheme.colors.text.primary : AghrbaLightTheme.colors.text.secondary,
                  fontWeight: activeTab === index ? 600 : 400,
                  transition: 'color 0.3s',
                  position: 'relative',
                  width: '25%',
                  textAlign: 'center',
                  '&:hover': {
                    color: activeTab === index ? AghrbaLightTheme.colors.text.primary : AghrbaLightTheme.colors.text.accent,
                  }
                }}
              >
                {tab.label}
              </div>
            ))}
            <div style={{ 
              position: 'absolute',
              bottom: 0,
              left: `calc(${activeTab * 25}% + 10px)`,
              width: 'calc(25% - 20px)',
              height: '2px',
              backgroundColor: AghrbaLightTheme.colors.secondary.main,
              transition: 'left 0.3s ease',
              borderRadius: '2px 2px 0 0',
            }} />
          </div>
          
          <div style={{ padding: '24px' }}>
            <Typography variant="body1">
              {tabs[activeTab].content}
            </Typography>
          </div>
        </Card>
      </div>
      
      {/* Form Elements */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '40px auto',
        padding: '0 20px',
      }}>
        <Typography variant="h5" style={{ marginBottom: '20px' }}>
          Form Elements
        </Typography>
        
        <Card style={{ padding: '30px' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: '24px'
          }}>
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Text Fields</Typography>
              
              <TextField
                label="Project Name"
                placeholder="Enter project name"
                style={{ marginBottom: '16px' }}
                fullWidth
              />
              
              <TextField
                label="Email Address"
                placeholder="your.email@example.com"
                type="email"
                fullWidth
                style={{ marginBottom: '16px' }}
              />
              
              <div style={{ display: 'flex', gap: '16px' }}>
                <TextField
                  label="First Name"
                  placeholder="First Name"
                  size="small"
                  style={{ flex: 1 }}
                />
                <TextField
                  label="Last Name"
                  placeholder="Last Name"
                  size="small"
                  style={{ flex: 1 }}
                />
              </div>
            </div>
            
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>Inputs with Magical Elements</Typography>
              
              <div style={{ 
                position: 'relative',
                marginBottom: '16px'
              }}>
                <TextField
                  label="Search with Magical Effect"
                  placeholder="Type to search..."
                  fullWidth
                />
                <div style={{ 
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: AghrbaLightTheme.colors.secondary.main,
                }}>
                  <MockIcons.Search />
                </div>
              </div>
              
              <div style={{ 
                marginBottom: '16px',
                position: 'relative'
              }}>
                <TextField
                  label="Password"
                  type="password"
                  placeholder="Enter secure password"
                  fullWidth
                />
                <div style={{ 
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: AghrbaLightTheme.colors.background.light,
                  color: AghrbaLightTheme.colors.text.secondary,
                  cursor: 'pointer',
                }}>
                  👁️
                </div>
              </div>
              
              <TextField
                label="Message"
                placeholder="Enter your message here..."
                fullWidth
                style={{ 
                  marginBottom: '16px',
                  height: '100px'
                }}
              />
            </div>
          </div>
        </Card>
      </div>
      
      {/* Sample Screens Section: Perfume Store Example (Light Mode) */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto',
        padding: '0 20px',
      }}>
        <Typography variant="h4" style={{ marginBottom: '20px' }}>
          Sample Implementation: Perfume Store (Light Mode)
        </Typography>
        
        <Card style={{ 
          padding: '0', 
          overflow: 'hidden',
          backgroundColor: 'transparent'
        }}>
          {/* Header */}
          <div style={{ 
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: AghrbaLightTheme.colors.background.paper,
            borderBottom: `1px solid ${AghrbaLightTheme.colors.background.light}`,
          }}>
            <Typography variant="h6" style={{ 
              ...styles.gradientText,
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              marginBottom: 0
            }}>
              Perf
            </Typography>
            
            <div style={{ display: 'flex', gap: '24px' }}>
              {['Home', 'Shop', 'Category', 'About Us', 'Contact Us'].map((item) => (
                <Typography 
                  key={item}
                  variant="body2" 
                  style={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      color: AghrbaLightTheme.colors.secondary.main,
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                color: AghrbaLightTheme.colors.text.secondary,
              }}>
                <MockIcons.Search />
              </div>
              <Button size="small">
                Cart
              </Button>
            </div>
          </div>
          
          {/* Hero Section */}
          <div style={{ 
            padding: '60px 40px',
            display: 'flex',
            alignItems: 'center',
            backgroundImage: `linear-gradient(135deg, ${AghrbaLightTheme.colors.background.default} 0%, ${AghrbaLightTheme.colors.background.paper} 100%)`,
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ 
              position: 'absolute',
              top: 0,
              right: 0,
              width: '60%',
              height: '100%',
              backgroundImage: 'radial-gradient(circle at 70% 50%, rgba(28, 70, 112, 0.1) 0%, transparent 70%)',
              zIndex: 1
            }}></div>
            
            <div style={{
              width: '50%',
              zIndex: 2
            }}>
              <Typography variant="h3" style={{ 
                fontFamily: '"Playfair Display", serif',
                marginBottom: '16px',
                ...styles.gradientText
              }}>
                Fall in love with Our Signature Perfumes
              </Typography>
              
              <Typography variant="body1" style={{ 
                marginBottom: '24px',
                color: AghrbaLightTheme.colors.text.secondary
              }}>
                Discover the perfect fragrance for you with our luxury perfume collection.
              </Typography>
              
              <Button 
                color="secondary" 
                size="large" 
                isGlowing 
                style={{ marginTop: '16px' }}
              >
                SHOP NOW
              </Button>
              
              <div style={{ 
                display: 'flex',
                gap: '24px',
                marginTop: '40px',
                color: AghrbaLightTheme.colors.text.secondary
              }}>
                <div>
                  <Typography variant="h5" style={{ 
                    color: AghrbaLightTheme.colors.secondary.main,
                    marginBottom: '4px'
                  }}>
                    90+
                  </Typography>
                  <Typography variant="body2">
                    Perfumes
                  </Typography>
                </div>
                
                <div>
                  <Typography variant="h5" style={{ 
                    color: AghrbaLightTheme.colors.secondary.main,
                    marginBottom: '4px'
                  }}>
                    15M+
                  </Typography>
                  <Typography variant="body2">
                    Customers
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          
          {/* Product Collection */}
          <div style={{ 
            padding: '60px 40px',
            backgroundColor: AghrbaLightTheme.colors.background.paper
          }}>
            <Typography variant="h4" style={{ 
              textAlign: 'center',
              marginBottom: '40px',
              fontFamily: '"Playfair Display", serif',
              color: AghrbaLightTheme.colors.secondary.main
            }}>
              Shop Our Wide Selection of Scents
            </Typography>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {[
                { name: 'Rosewood Elixir', price: '$80.00' },
                { name: 'Citrus Zest', price: '$90.00' },
                { name: 'Woodland Myst', price: '$70.00' },
                { name: 'Rosewood Brew', price: '$80.00' },
                { name: 'Citrus Zest', price: '$70.00' },
                { name: 'Woodland Myst', price: '$100.00' }
              ].map((product, index) => (
                <Card key={index} isGlowing style={{ 
                  padding: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '12px',
                  backgroundColor: AghrbaLightTheme.colors.background.light,
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    width: '80px',
                    height: '120px',
                    backgroundColor: AghrbaLightTheme.colors.primary.light,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                  }}></div>
                  
                  <Typography variant="subtitle2">
                    {product.name}
                  </Typography>
                  
                  <Typography variant="body2" style={{ color: AghrbaLightTheme.colors.secondary.main }}>
                    {product.price}
                  </Typography>
                </Card>
              ))}
            </div>
          </div>
          
          {/* Reviews Section */}
          <div style={{ 
            padding: '40px',
            backgroundColor: AghrbaLightTheme.colors.background.default
          }}>
            <Typography variant="h5" style={{ 
              textAlign: 'center',
              marginBottom: '24px',
              color: AghrbaLightTheme.colors.secondary.main
            }}>
              Customer Reviews
            </Typography>
            
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              {[
                { rating: 5, text: 'Absolutely love Rosewood Elixir! The scent is intoxicating, complex yet light.' },
                { rating: 5, text: 'Citrus Zest is my go-to summer fragrance. It\'s refreshing, long-lasting, with good value.' }
              ].map((review, index) => (
                <Card key={index} gradientBorder style={{ padding: '24px' }}>
                  <div style={{ 
                    display: 'flex',
                    marginBottom: '12px',
                    color: AghrbaLightTheme.colors.secondary.main
                  }}>
                    {'★★★★★'.slice(0, review.rating)}
                  </div>
                  
                  <Typography variant="body2">
                    {review.text}
                  </Typography>
                </Card>
              ))}
            </div>
          </div>
        </Card>
      </div>
      
      {/* Theme Comparison */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto',
        padding: '0 20px',
      }}>
        <Typography variant="h4" style={{ marginBottom: '30px' }}>
          Theme Comparison: Dark vs Light
        </Typography>
        
        <Card style={{ padding: '30px' }}>
          <Typography variant="h6" style={{ marginBottom: '20px' }}>
            Key Differences
          </Typography>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {/* Background & Text */}
            <div>
              <Typography variant="subtitle1" style={{ color: AghrbaLightTheme.colors.secondary.main, marginBottom: '8px' }}>
                Background & Text
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                <strong>Dark Theme:</strong> Deep navy backgrounds (#071725, #0A243A) with white and light blue text for contrast.
              </Typography>
              <Typography variant="body2">
                <strong>Light Theme:</strong> Bright backgrounds (#F8FAFD, #FFFFFF) with dark blue text for readability.
              </Typography>
            </div>
            
            {/* Colors & Contrast */}
            <div>
              <Typography variant="subtitle1" style={{ color: AghrbaLightTheme.colors.secondary.main, marginBottom: '8px' }}>
                Colors & Contrast
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                <strong>Dark Theme:</strong> Higher contrast with gold elements standing out dramatically against dark backgrounds.
              </Typography>
              <Typography variant="body2">
                <strong>Light Theme:</strong> Subtler contrast with navy and gold elements providing elegant accents on light backgrounds.
              </Typography>
            </div>
            
            {/* Shadows & Effects */}
            <div>
              <Typography variant="subtitle1" style={{ color: AghrbaLightTheme.colors.secondary.main, marginBottom: '8px' }}>
                Shadows & Effects
              </Typography>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>
                <strong>Dark Theme:</strong> Deeper shadows and more vibrant glow effects for magical elements.
              </Typography>
              <Typography variant="body2">
                <strong>Light Theme:</strong> Subtler shadows and refined glow effects that complement the lighter aesthetic.
              </Typography>
            </div>
          </div>
          
          <Typography variant="h6" style={{ margin: '30px 0 20px' }}>
            When to Use Each Theme
          </Typography>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '20px'
          }}>
            <div>
              <Typography variant="subtitle1" style={{ color: AghrbaLightTheme.colors.secondary.main, marginBottom: '8px' }}>
                Dark Theme Ideal For:
              </Typography>
              <ul style={{ paddingLeft: '20px', margin: 0 }}>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Evening use and low-light environments
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Emphasizing the magical and mystical brand elements
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Creating an immersive, focused experience
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Marketing materials that need dramatic impact
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Users who prefer dark mode for reduced eye strain
                  </Typography>
                </li>
              </ul>
            </div>
            
            <div>
              <Typography variant="subtitle1" style={{ color: AghrbaLightTheme.colors.secondary.main, marginBottom: '8px' }}>
                Light Theme Ideal For:
              </Typography>
              <ul style={{ paddingLeft: '20px', margin: 0 }}>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Daytime use and bright environments
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Business and professional contexts
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Content-heavy interfaces requiring extended reading
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '4px' }}>
                    Accessibility needs for users with certain visual impairments
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    Interfaces where printability is important
                  </Typography>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Implementation Guidelines */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto',
        padding: '0 20px',
      }}>
        <Typography variant="h4" style={{ marginBottom: '30px' }}>
          Theme Implementation Guidelines
        </Typography>
        
        <Card style={{ padding: '30px' }}>
          <Typography variant="body2" style={{ marginBottom: '20px' }}>
            To implement theme switching in your application, follow these best practices:
          </Typography>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
            gap: '30px'
          }}>
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>
                Theme Provider Setup
              </Typography>
              
              <div style={{ 
                backgroundColor: AghrbaLightTheme.colors.background.light,
                padding: '16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: AghrbaLightTheme.colors.text.primary,
                marginBottom: '20px'
              }}>
                <pre style={{ margin: 0, overflowX: 'auto' }}>
{`// src/ThemeProvider.jsx
import React, { useState, createContext, useContext } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { darkTheme, lightTheme } from './themes';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [themeMode, setThemeMode] = useState('dark');
  
  const toggleTheme = () => {
    setThemeMode(prev => prev === 'dark' ? 'light' : 'dark');
  };
  
  const theme = themeMode === 'dark' ? darkTheme : lightTheme;
  
  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};`}
                </pre>
              </div>
              
              <Typography variant="h6" style={{ marginBottom: '16px' }}>
                Theme Toggle Component
              </Typography>
              
              <div style={{ 
                backgroundColor: AghrbaLightTheme.colors.background.light,
                padding: '16px',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                color: AghrbaLightTheme.colors.text.primary
              }}>
                <pre style={{ margin: 0, overflowX: 'auto' }}>
{`// src/components/ThemeToggle.jsx
import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useTheme } from '../ThemeProvider';

const ThemeToggle = () => {
  const { themeMode, toggleTheme } = useTheme();
  
  return (
    <Tooltip title={\`Switch to \${themeMode === 'dark' ? 'light' : 'dark'} mode\`}>
      <IconButton onClick={toggleTheme} color="inherit">
        {themeMode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;`}
                </pre>
              </div>
            </div>
            
            <div>
              <Typography variant="h6" style={{ marginBottom: '16px' }}>
                Best Practices
              </Typography>
              
              <ul style={{ paddingLeft: '20px', margin: 0 }}>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>User Preference Detection:</strong> Automatically detect and apply the user's system preference for light/dark mode initially.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>Persistent Preference:</strong> Store the user's theme preference in localStorage to maintain consistency between sessions.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>Smooth Transitions:</strong> Add subtle transitions when switching themes to avoid jarring visual changes.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>Consistent Colors:</strong> Use semantic color variables (primary, secondary, etc.) instead of hard-coded hex values to ensure proper theme switching.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>Image Adaptations:</strong> Provide alternative images optimized for each theme when necessary (especially for illustrations and icons).
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2" style={{ marginBottom: '8px' }}>
                    <strong>Testing:</strong> Test all components in both themes to ensure optimal appearance and readability.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body2">
                    <strong>Accessible Toggle:</strong> Position the theme toggle in a consistent, easily accessible location throughout the interface.
                  </Typography>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
      
      {/* Footer */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '60px auto 40px',
        padding: '0 20px',
        textAlign: 'center'
      }}>
        <Typography variant="h4" style={{ 
          marginBottom: '16px',
          ...styles.gradientText
        }}>
          Create Your Magical Experience
        </Typography>
        
        <Typography variant="body1" style={{ 
          color: AghrbaLightTheme.colors.text.secondary,
          maxWidth: '700px',
          margin: '0 auto',
          marginBottom: '24px'
        }}>
          This light mode design system provides a fresh, airy alternative to our dark theme while maintaining the magical elements that define the Aghrba brand. Use both themes to create versatile, enchanting interfaces for all users and contexts.
        </Typography>
        
        <Button 
          color="secondary" 
          size="large" 
          isGlowing 
          style={{ margin: '0 auto' }}
        >
          Get Started
        </Button>
      </div>
    </div>
  );
};

export default AghrbaLightDesignSystem;